// VoiceInputButton — tap-to-record mic button that transcribes via the worker.
// Owner: voice-input-dev.
//
// Props:
//   token         — invitation token (used in POST /api/invitations/:token/transcribe)
//   onTranscript  — called with the returned transcript string on success
//   disabled      — external disable flag (e.g. submitting the form)
//
// The button hides itself when the browser cannot record (missing
// mediaDevices, insecure context, ancient browser) OR when the user has
// previously denied microphone permission for this session.

import * as React from 'react'
import { Mic, Square, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getStoredLang } from '@/lib/api'
import { t as tr } from '@/lib/i18n'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'

const MAX_RECORDING_MS = 30 * 60_000 // 30 minutes
const MAX_BLOB_BYTES = 25 * 1024 * 1024 // 25 MB — matches server cap

type Status = 'idle' | 'requesting' | 'recording' | 'transcribing'

export interface VoiceInputButtonProps {
  token: string
  onTranscript: (text: string) => void
  disabled?: boolean
}

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ]
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t
  }
  return ''
}

export function VoiceInputButton({
  token,
  onTranscript,
  disabled,
}: VoiceInputButtonProps) {
  const { toast } = useToast()
  const [canRecord, setCanRecord] = React.useState(false)
  const [status, setStatus] = React.useState<Status>('idle')
  const [elapsed, setElapsed] = React.useState(0)

  const recorderRef = React.useRef<MediaRecorder | null>(null)
  const chunksRef = React.useRef<Blob[]>([])
  const streamRef = React.useRef<MediaStream | null>(null)
  const autoStopRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAtRef = React.useRef<number>(0)
  const mimeRef = React.useRef<string>('')
  const cancelledRef = React.useRef<boolean>(false)

  // Feature detection: hide if unsupported or insecure context.
  React.useEffect(() => {
    const hasApi =
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof window !== 'undefined' &&
      typeof window.MediaRecorder !== 'undefined'
    setCanRecord(hasApi)
  }, [])

  const cleanupStream = React.useCallback(() => {
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current)
      autoStopRef.current = null
    }
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    recorderRef.current = null
    chunksRef.current = []
  }, [])

  React.useEffect(() => () => cleanupStream(), [cleanupStream])

  const sendAudio = React.useCallback(
    async (blob: Blob) => {
      if (blob.size === 0) {
        toast({
          title: 'No audio captured.',
          description: 'Try again — make sure the mic is working.',
        })
        setStatus('idle')
        return
      }
      if (blob.size > MAX_BLOB_BYTES) {
        toast({
          title: 'Recording too large.',
          description: 'Please keep recordings under 30 minutes.',
        })
        setStatus('idle')
        return
      }

      setStatus('transcribing')
      try {
        const fd = new FormData()
        fd.append('audio', blob, 'recording.webm')
        const res = await fetch(
          `/api/invitations/${encodeURIComponent(token)}/transcribe`,
          { method: 'POST', body: fd },
        )

        if (res.status === 429) {
          toast({
            title: 'Transcription limit reached.',
            description:
              "You've used all transcription attempts for this invitation; please type the rest.",
          })
          setStatus('idle')
          return
        }
        if (!res.ok) {
          toast({
            title: 'Transcription failed.',
            description: 'You can type your answer instead.',
          })
          setStatus('idle')
          return
        }

        const data = (await res.json()) as { transcript?: string }
        if (data.transcript && data.transcript.length > 0) {
          onTranscript(data.transcript)
        } else {
          toast({
            title: 'Nothing was transcribed.',
            description: 'You can type your answer instead.',
          })
        }
      } catch (err) {
        // Surface enough detail for the respondent to screenshot / the
        // console to capture. The earlier silent generic toast made
        // diagnosis of long-recording failures impossible.
        const e = err as Error & { name?: string; code?: string }
        const detail = `${e?.name ?? 'Error'}: ${e?.message ?? 'unknown'}`
        console.error('[VoiceInput] fetch failed', { err, blobSize: blob.size, blobType: blob.type })
        toast({
          title: 'Connection dropped.',
          description: `${detail}. You can type your answer instead.`,
        })
      } finally {
        setStatus('idle')
      }
    },
    [token, onTranscript, toast],
  )

  const startRecording = React.useCallback(async () => {
    if (status !== 'idle' || disabled) return
    setStatus('requesting')
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      // Permission denied / unavailable: hide the button for the rest of the
      // session rather than leaving a non-functional control in place.
      setCanRecord(false)
      setStatus('idle')
      return
    }

    const mime = pickMimeType()
    let recorder: MediaRecorder
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream)
    } catch {
      stream.getTracks().forEach((t) => t.stop())
      toast({
        title: 'Recording not supported.',
        description: 'Your browser cannot record audio here.',
      })
      setStatus('idle')
      return
    }

    streamRef.current = stream
    recorderRef.current = recorder
    chunksRef.current = []
    mimeRef.current = mime || recorder.mimeType || 'audio/webm'
    cancelledRef.current = false

    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data)
    }
    recorder.onstop = () => {
      const wasCancelled = cancelledRef.current
      const type = mimeRef.current.split(';')[0] || 'audio/webm'
      const blob = new Blob(chunksRef.current, { type })
      cleanupStream()
      if (wasCancelled) {
        setStatus('idle')
        return
      }
      void sendAudio(blob)
    }
    recorder.onerror = () => {
      toast({
        title: 'Recording error.',
        description: 'Please try again.',
      })
      cleanupStream()
      setStatus('idle')
    }

    startedAtRef.current = Date.now()
    setElapsed(0)
    recorder.start()
    setStatus('recording')

    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000))
    }, 500)

    autoStopRef.current = setTimeout(() => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop()
      }
    }, MAX_RECORDING_MS)
  }, [status, disabled, toast, cleanupStream, sendAudio])

  const stopRecording = React.useCallback(() => {
    cancelledRef.current = false
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop()
    }
  }, [])

  const cancelRecording = React.useCallback(() => {
    cancelledRef.current = true
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop()
    } else {
      cleanupStream()
      setStatus('idle')
    }
  }, [cleanupStream])

  if (!canRecord) return null

  const isBusy = status === 'transcribing' || status === 'requesting'

  if (status === 'recording') {
    return (
      <div className="inline-flex items-center gap-1">
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={stopRecording}
          title="Stop and transcribe"
          aria-label="Stop and transcribe"
          className="gap-2"
        >
          <Square className="h-4 w-4" />
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 rounded-full bg-white animate-pulse"
          />
          <span className="tabular-nums">{elapsed}s</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={cancelRecording}
          title="Cancel recording"
          aria-label="Cancel recording"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={disabled || isBusy}
      onClick={startRecording}
      aria-label={tr(getStoredLang(), 'mic_aria')}
      className={cn(
        'gap-2 text-muted-foreground hover:text-foreground',
        'hover:bg-secondary/60 h-8 px-2',
      )}
    >
      {status === 'transcribing' ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{tr(getStoredLang(), 'voice_transcribing')}</span>
        </>
      ) : status === 'requesting' ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{tr(getStoredLang(), 'voice_requesting')}</span>
        </>
      ) : (
        <>
          <Mic className="h-4 w-4" />
          <span>{tr(getStoredLang(), 'voice_message')}</span>
        </>
      )}
    </Button>
  )
}

export default VoiceInputButton
